"use client";

import { useEffect, useState } from "react";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import type { Lang } from "@/hooks/localization/getTranslation";
import { PagesPaths } from "@/utils/functions/pages-paths";

interface Props {
  /** Seconds until the automatic refresh (already jittered by the server). */
  seconds: number;
  lang: Lang;
  retryingInLabel: string;
  refreshLabel: string;
}

/**
 * Reached two ways: a proxy rewrite keeps the visitor's own URL, so a reload returns them
 * to the page they asked for; a server redirect leaves them ON /company-error, where a
 * reload would only show this page again — send those home, like RetryButton.
 */
const refreshStorefront = () => {
  if (window.location.pathname.startsWith(PagesPaths.companyError)) {
    window.location.assign("/");
  } else {
    window.location.reload();
  }
};

// Latin digits to match prices elsewhere in the store; long units read naturally
// ("45 seconds" / "45 ثانية") and announce well to screen readers.
const formatRemaining = (seconds: number, lang: Lang) => {
  const inMinutes = seconds >= 60;
  return new Intl.NumberFormat(`${lang}-u-nu-latn`, {
    style: "unit",
    unit: inMinutes ? "minute" : "second",
    unitDisplay: "long",
  }).format(inMinutes ? Math.ceil(seconds / 60) : seconds);
};

const MaintenanceCountdown = ({
  seconds,
  lang,
  retryingInLabel,
  refreshLabel,
}: Props) => {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    // Measured against a fixed deadline, not by counting ticks: background tabs throttle
    // timers, so a tick counter would drift far behind the real window.
    const deadline = Date.now() + seconds * 1000;
    let refreshed = false;

    // A hidden tab waits until it is looked at again — no point loading a page nobody
    // sees, and it keeps parked background tabs from adding load on recovery.
    const refreshWhenVisible = () => {
      if (refreshed || document.visibilityState !== "visible") return;
      refreshed = true;
      refreshStorefront();
    };

    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) {
        window.clearInterval(timer);
        refreshWhenVisible();
      }
    };

    const timer = window.setInterval(tick, 1000);
    const onVisibilityChange = () => {
      if (Date.now() >= deadline) refreshWhenVisible();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [seconds]);

  return (
    <>
      {/* role="timer" is implicitly aria-live="off": the per-second update is not
          announced, but the value is there when a screen reader reaches it.
          suppressHydrationWarning: Node's ICU and the phone's can word the unit
          differently (Arabic plural forms especially); the client's wording wins. */}
      <p className="m-0 text-center" role="timer" suppressHydrationWarning>
        {retryingInLabel} {formatRemaining(remaining, lang)}
      </p>
      <CustomButton
        type={BtnTypes.primary}
        size={BtnSizes.medium}
        radius={BtnRadius.medium}
        onClick={refreshStorefront}
      >
        {refreshLabel}
      </CustomButton>
    </>
  );
};

export default MaintenanceCountdown;
